import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useDebounce } from "hooks/useDebounce"
import { previewGuideBookingPayment, previewGuideOrderPayment } from "store/bookings"

// Long enough to skip the keystrokes of a typed amount, short enough to feel immediate.
const PREVIEW_DELAY_MS = 400

/**
 * The server's quote for collecting `amount` (+ `tipAmount`) through `paymentProvider`:
 * { platform_fee, card_fee, total, grand_total, remaining_balance_after, ... }.
 *
 * - `preview`: the last answer received, kept on screen while a newer one is on its way so the
 *   rows don't flicker. It can belong to earlier inputs, so never charge from it alone.
 * - `isPending`: the inputs have no answer yet — during the debounce wait AND while the request is
 *   out. Block the charge on this, so nothing is submitted on a stale quote.
 * - `isLoading`: a request for the current inputs is actually in flight. Drives the spinner, which
 *   therefore only appears once the call is made, not on every keystroke.
 * A slower reply to an older request is ignored. Pass `isEnabled: false` when there is nothing to
 * quote. With `orderUuid` the quote is for the whole order's balance instead of one booking.
 */
export const usePaymentPreview = ({ bookingId, orderUuid, amount, paymentProvider, tipAmount, isEnabled }) => {
  const dispatch = useDispatch()
  const requestKey = isEnabled ? JSON.stringify([bookingId, orderUuid ?? null, Number(amount) || 0, paymentProvider, Number(tipAmount) || 0]) : null
  const debouncedKey = useDebounce(requestKey, PREVIEW_DELAY_MS)
  const [result, setResult] = useState({ key: null, preview: null, isError: false })
  const [inFlightKey, setInFlightKey] = useState(null)
  // Bumped by retry() to re-send the same request after a failure.
  const [attempt, setAttempt] = useState(0)
  const latestKey = useRef(null)

  useEffect(() => {
    latestKey.current = debouncedKey
    if (!debouncedKey) return
    const [id, order, requestAmount, provider, tip] = JSON.parse(debouncedKey)
    const settle = (next) => {
      if (latestKey.current !== debouncedKey) return
      setResult(next)
      setInFlightKey(null)
    }
    setInFlightKey(debouncedKey)
    const data = { amount: requestAmount, payment_provider: provider, tip_amount: tip }
    dispatch(order ? previewGuideOrderPayment(order, data) : previewGuideBookingPayment(id, data))
      .then((response) => settle({ key: debouncedKey, preview: response?.data ?? null, isError: !response?.data }))
      .catch(() => settle({ key: debouncedKey, preview: null, isError: true }))
  }, [debouncedKey, attempt, dispatch])

  const isCurrent = Boolean(requestKey) && result.key === requestKey
  return {
    preview: requestKey ? result.preview : null,
    isPending: Boolean(requestKey) && !isCurrent,
    isLoading: Boolean(requestKey) && inFlightKey === requestKey,
    isError: isCurrent && result.isError,
    retry: () => {
      setResult((previous) => ({ ...previous, key: null, isError: false }))
      setAttempt((count) => count + 1)
    }
  }
}
